import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Content } from "../../organisms/MainPage/Content";
import { NavPanel } from "../../organisms/MainPage/NavPanel";
import { MobileHeader, MobileNavDrawer, MobileHeaderSlotContext } from "../../organisms/MainPage/MobileNavigation";
import ProfileOverview from "../../organisms/ProfileOverview";

import './MainPage.scss';

import {
    getMainNavigationTabs,
    getTabDefinitionById,
    isPrimaryNavigationTab,
} from "../../../constants/MainNavigationTabs";
import { useBackgroundVariant } from "../../../store/BackgroundVariantContext";
import { DEFAULT_BACKGROUND_VARIANT } from "../../../constants/appBackgroundVariants";
import { DESKTOP_BREAKPOINT, mediaQueryMinWidth } from "../../../constants/breakpoints";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { ACTIVE_TAB_STORAGE_KEY, NAVIGATE_TAB_EVENT, TAB_IDS } from "../../../constants/navigation";
import { CHAT_SELECTED_CHAT_STORAGE_KEY } from "../../../constants/chat";
import { stageChatNavigationIntent } from "../../../utils/chatNavigationIntent";
import { isBandsRoutePath } from "../../../utils/bandRoutes";
import { getProfileRouteLocator, isProfileRoutePath, normalizeProfileUserId } from "../../../utils/profileRoutes";
import userService from "../../../services/userService";

// Key for storing active tab in localStorage
const TAB_ROUTE_MAP = Object.freeze({
    [TAB_IDS.DISCOVER]: '/discover',
    [TAB_IDS.EVENTS]: '/events',
    [TAB_IDS.VENUE]: '/venues',
    [TAB_IDS.BANDS]: '/bands',
    [TAB_IDS.BILLING]: '/billing',
    [TAB_IDS.SYSTEM]: '/system',
});
const ROUTE_TAB_MAP = Object.freeze(
    Object.entries(TAB_ROUTE_MAP).reduce((acc, [tabId, path]) => {
        acc[path] = tabId;
        return acc;
    }, {})
);

const getRouteTabId = (pathname) => {
    if (isBandsRoutePath(pathname)) {
        return TAB_IDS.BANDS;
    }
    return ROUTE_TAB_MAP[pathname] || null;
};

export function MainPage() {
    const { user: currentUser } = useSelector((state) => state.auth);
    const { t } = useTranslation();
    const currentUserRoles = currentUser?.roles;
    const location = useLocation();
    const navigate = useNavigate();
    const routeProfileLocator = useMemo(
        () => getProfileRouteLocator(location.pathname),
        [location.pathname]
    );
    const isProfileRoute = useMemo(
        () => isProfileRoutePath(location.pathname),
        [location.pathname]
    );
    const [usernameRouteResolution, setUsernameRouteResolution] = useState({
        username: null,
        userId: null,
        isLoading: false,
        notFound: false,
    });
    const routeProfileUserId = routeProfileLocator?.type === "userId"
        ? routeProfileLocator.value
        : usernameRouteResolution.userId;
    const routeProfileUsername = routeProfileLocator?.type === "username"
        ? routeProfileLocator.value
        : null;

    const availableTabs = useMemo(
        () => getMainNavigationTabs(currentUserRoles),
        [currentUserRoles]
    );
    const primaryNavigationTabs = useMemo(
        () => availableTabs.filter(isPrimaryNavigationTab),
        [availableTabs]
    );
    const [activeTab, setActiveTab] = useState(null);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    const [mobileHeaderSlot, setMobileHeaderSlot] = useState(null);
    const isDesktop = useMediaQuery(mediaQueryMinWidth(DESKTOP_BREAKPOINT));

    useEffect(() => {
        if (!availableTabs.length) {
            return;
        }

        if (isProfileRoute) {
            return;
        }

        const routeTabId = getRouteTabId(location.pathname);
        if (!routeTabId) {
            return;
        }

        const isTabAvailable = availableTabs.some((tab) => tab.id === routeTabId);
        if (!isTabAvailable) {
            if (location.pathname !== '/') {
                navigate('/', { replace: true });
            }
            return;
        }

        if (routeTabId !== activeTab) {
            setActiveTab(routeTabId);
        }
    }, [activeTab, availableTabs, isProfileRoute, location.pathname, navigate]);

    useEffect(() => {
        if (isProfileRoute && !routeProfileLocator) {
            navigate('/', { replace: true });
        }
    }, [isProfileRoute, navigate, routeProfileLocator]);

    useEffect(() => {
        if (routeProfileLocator?.type !== "username") {
            setUsernameRouteResolution({
                username: null,
                userId: null,
                isLoading: false,
                notFound: false,
            });
            return undefined;
        }

        const username = routeProfileLocator.value;
        let isCancelled = false;
        setUsernameRouteResolution({
            username,
            userId: null,
            isLoading: true,
            notFound: false,
        });

        userService.fetchUserByUsername(username)
            .then((user) => {
                if (isCancelled) {
                    return;
                }
                const userId = normalizeProfileUserId(user?.id);
                setUsernameRouteResolution({
                    username,
                    userId,
                    isLoading: false,
                    notFound: user === null,
                });
            })
            .catch((error) => {
                if (!isCancelled) {
                    setUsernameRouteResolution({
                        username,
                        userId: null,
                        isLoading: false,
                        notFound: error?.response?.status === 404,
                    });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [routeProfileLocator]);

    useEffect(() => {
        if (
            isProfileRoute
            && routeProfileLocator?.type === "username"
            && usernameRouteResolution.username === routeProfileLocator.value
            && usernameRouteResolution.notFound
            && !usernameRouteResolution.isLoading
        ) {
            navigate('/', { replace: true });
        }
    }, [isProfileRoute, navigate, routeProfileLocator, usernameRouteResolution]);

    useEffect(() => {
        if (!availableTabs.length) {
            return;
        }

        if (getRouteTabId(location.pathname)) {
            return;
        }

        setActiveTab((current) => {
            if (current && primaryNavigationTabs.some((tab) => tab.id === current)) {
                return current;
            }

            const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
            const defaultTab = primaryNavigationTabs[0]?.id || availableTabs[0].id;
            if (savedTab && primaryNavigationTabs.some((tab) => tab.id === savedTab)) {
                return savedTab;
            }
            return defaultTab;
        });
    }, [availableTabs, location.pathname, primaryNavigationTabs]);

    useEffect(() => {
        if (activeTab) {
            localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
        }
    }, [activeTab]);

    const navigateToTabPath = useCallback((tabId, options = {}) => {
        const replace = options?.replace === true;
        const targetPath = TAB_ROUTE_MAP[tabId];

        if (isProfileRoute) {
            const nextPath = targetPath || '/';
            if (location.pathname !== nextPath) {
                if (replace) {
                    navigate(nextPath, { replace: true });
                } else {
                    navigate(nextPath);
                }
            }
            return;
        }

        if (targetPath) {
            if (location.pathname !== targetPath) {
                if (replace) {
                    navigate(targetPath, { replace: true });
                } else {
                    navigate(targetPath);
                }
            }
            return;
        }

        if (getRouteTabId(location.pathname) && location.pathname !== '/') {
            if (replace) {
                navigate('/', { replace: true });
            } else {
                navigate('/');
            }
        }
    }, [isProfileRoute, location.pathname, navigate]);

    useEffect(() => {
        if (!activeTab) {
            return;
        }

        const routeTabId = getRouteTabId(location.pathname);
        if (routeTabId === activeTab) {
            return;
        }
        if (routeTabId && routeTabId !== activeTab) {
            return;
        }

        if (isProfileRoute) {
            return;
        }

        navigateToTabPath(activeTab, { replace: true });
    }, [activeTab, isProfileRoute, location.pathname, navigateToTabPath]);

    const { setVariant } = useBackgroundVariant()

    const activeTabDefinition = useMemo(() => getTabDefinitionById(activeTab), [activeTab]);

    useEffect(() => {
        const handler = (event) => {
            const targetTab = event?.detail?.tabId;
            if (!targetTab) {
                return;
            }
            const isTabAvailable = availableTabs.some((tab) => tab.id === targetTab);
            if (!isTabAvailable) {
                return;
            }

            const explicitPath = typeof event?.detail?.path === "string" ? event.detail.path : null;
            const currentRouteTab = getRouteTabId(location.pathname);
            setActiveTab(targetTab);
            try {
                const requestedChatId = event?.detail?.chatId;
                if (targetTab === TAB_IDS.CHAT && requestedChatId !== undefined && requestedChatId !== null) {
                    const normalizedChatId = String(requestedChatId).trim();
                    if (normalizedChatId) {
                        if (activeTab !== TAB_IDS.CHAT) {
                            stageChatNavigationIntent(normalizedChatId);
                        }
                        window.localStorage.setItem(CHAT_SELECTED_CHAT_STORAGE_KEY, normalizedChatId);
                    }
                }
                window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, targetTab);
            } catch (_) {
                // ignore storage errors
            }

            if (isProfileRoute) {
                if (explicitPath && getRouteTabId(explicitPath) === targetTab) {
                    navigate(explicitPath);
                } else {
                    navigateToTabPath(targetTab);
                }
                return;
            }

            if (explicitPath && getRouteTabId(explicitPath) === targetTab) {
                if (location.pathname !== explicitPath) {
                    navigate(explicitPath);
                }
                return;
            }

            if (currentRouteTab && currentRouteTab !== targetTab) {
                navigateToTabPath(targetTab);
            }
        };
        window.addEventListener(NAVIGATE_TAB_EVENT, handler);
        return () => {
            window.removeEventListener(NAVIGATE_TAB_EVENT, handler);
        };
    }, [activeTab, availableTabs, isProfileRoute, location.pathname, navigate, navigateToTabPath, setActiveTab]);

    useEffect(() => {
        setVariant(activeTabDefinition?.backgroundVariant ?? DEFAULT_BACKGROUND_VARIANT)
    }, [activeTabDefinition, setVariant])

    useEffect(() => () => {
        setVariant(DEFAULT_BACKGROUND_VARIANT)
    }, [setVariant])

    useEffect(() => {
        if (isDesktop) {
            setIsMobileDrawerOpen(false);
        }
    }, [isDesktop]);

    const handleTabChange = useCallback((tabId) => {
        // Persist the selection synchronously before navigating. Switching
        // between a band route (e.g. /bands/@handle) and a non-band route
        // (e.g. / for Home/Chat) swaps which <Route> element renders MainPage,
        // remounting it and resetting `activeTab`. Without an eager write the
        // remounted MainPage would restore the stale tab from localStorage
        // (still "Bands") and bounce the user back to /bands. The
        // NAVIGATE_TAB_EVENT handler persists eagerly for the same reason.
        try {
            window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tabId);
        } catch (_) {
            // ignore storage errors
        }
        setActiveTab(tabId);
        navigateToTabPath(tabId);
        if (!isDesktop) {
            setIsMobileDrawerOpen(false);
        }
    }, [isDesktop, navigateToTabPath]);

    const handleRouteProfileClose = useCallback(() => {
        const nextPath = TAB_ROUTE_MAP[activeTab] || '/';
        navigate(nextPath, { replace: true });
    }, [activeTab, navigate]);

    if (!availableTabs.length || !activeTab) {
        return null;
    }

    const activeTabLabel = activeTabDefinition?.labelKey
        ? t(activeTabDefinition.labelKey)
        : activeTabDefinition?.label;
    // Only the Feed page shows a centered header title; other tab-less pages
    // keep just the hamburger.
    const mobileHeaderLabel = activeTab === TAB_IDS.FEED ? activeTabLabel : undefined;

    return (
        <div id="mainPage" className={isDesktop ? 'mainPage--desktop' : 'mainPage--mobile'}>
            {!isDesktop && (
                <MobileHeader
                    isOpen={isMobileDrawerOpen}
                    onToggle={() => setIsMobileDrawerOpen((open) => !open)}
                    slotRef={setMobileHeaderSlot}
                    activeLabel={mobileHeaderLabel}
                />
            )}
            <MobileHeaderSlotContext.Provider value={isDesktop ? null : mobileHeaderSlot}>
                <div id="body">
                    {isDesktop && (
                        <div id="navMenu">
                            <NavPanel tabs={primaryNavigationTabs} activeTab={activeTab} onClick={handleTabChange}/>
                        </div>
                    )}
                    <div id="content">
                        <Content activeTab={activeTab} tabs={availableTabs}/>
                    </div>
                </div>
            </MobileHeaderSlotContext.Provider>
            {!isDesktop && (
                <MobileNavDrawer
                    isOpen={isMobileDrawerOpen}
                    tabs={primaryNavigationTabs}
                    activeTab={activeTab}
                    onSelect={handleTabChange}
                    onClose={() => setIsMobileDrawerOpen(false)}
                />
            )}
            {routeProfileUserId && (
                <ProfileOverview
                    userId={routeProfileUserId}
                    profileUsername={routeProfileUsername}
                    onClose={handleRouteProfileClose}
                />
            )}
        </div>
    )
}
